import { useState, useEffect } from 'react';
import { Sheet, Tabs, Button, Badge } from '../ui';
import { 
  Bookmark, 
  Send, 
  Building, 
  MapPin, 
  Clock,
  DollarSign,
  Users,
  Target,
  FileText,
  TrendingUp,
  Award
} from 'lucide-react';
import type { Job, SavedJob, JobMatch } from '../../types';

interface JobDetailSheetProps {
  job: Job | SavedJob | JobMatch | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (jobId: string) => void;
  onReject?: (jobId: string) => void;
  onApply?: (jobId: string) => void;
}

export function JobDetailSheet({
  job,
  isOpen,
  onClose,
  onSave,
  onReject,
  onApply,
}: JobDetailSheetProps) {
  const [activeTab, setActiveTab] = useState('description');
  const [telemetryStart, setTelemetryStart] = useState<number | null>(null);

  // Track sheet open performance (target: 200ms)
  useEffect(() => {
    if (isOpen) {
      setTelemetryStart(Date.now());
    }
  }, [isOpen]);

  if (!job) {
    return null;
  }

  const formatSalary = (min?: number, max?: number): string => {
    if (!min && !max) return '';
    
    const formatAmount = (amount: number) => {
      if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
      if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}k`;
      return `$${amount.toLocaleString()}`;
    };

    if (min && max) {
      return `${formatAmount(min)} - ${formatAmount(max)}`;
    }
    if (min) {
      return `${formatAmount(min)}+`;
    }
    return `Up to ${formatAmount(max!)}`;
  };

  // Telemetry for all actions
  const handleAction = (action: string, data?: any) => {
    const telemetry = {
      action,
      jobId: job.id,
      openTime: telemetryStart ? Date.now() - telemetryStart : null,
      activeTab,
      ...data
    };
    console.log('Job action telemetry:', telemetry);
    // TODO: Send to analytics service
  };

  const handleSave = () => {
    handleAction('save');
    onSave?.(job.id);
  };

  const handleApply = () => {
    handleAction('apply');
    onApply?.(job.id);
  };

  const handleReject = () => {
    handleAction('reject');
    onReject?.(job.id);
    onClose();
  };

  const handleGenerateResume = () => {
    handleAction('generate_resume');
    // TODO: Navigate to resume editor with job context
  };

  const handleTeach = (feedback: string) => {
    handleAction('teach', { feedback });
    // TODO: Send feedback to strategy system
  };

  const isMatch = 'match_score' in job;
  
  // Mock data for fit analysis
  const resumeOverlap = isMatch ? (job as JobMatch).match_score * 100 : 78;
  const mustHaves = { total: 5, matched: 4 };
  const gaps = ['Scala', 'GraphQL'];
  const strategy = job.attribution?.strategy_key || 'HIGH_COMP';
  const confidence = job.attribution?.confidence_score || 0.82;

  const tabItems = [
    {
      label: 'Description',
      value: 'description',
      content: (
        <div className="space-y-6">
          {/* Company Info */}
          <div className="card">
            <div className="card-content">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Building className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">{job.company}</h3>
                  <div className="text-sm text-muted-foreground space-y-1 mt-1">
                    <div className="flex items-center gap-1"><Users className="h-3 w-3" /> Technology</div>
                    <div className="flex items-center gap-1"><Target className="h-3 w-3" /> 100-500 employees</div>
                    <div className="flex items-center gap-1"><Clock className="h-3 w-3" /> Founded 2018</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Job Details */}
          <div className="card">
            <div className="card-content">
              <h4 className="font-medium mb-3">Job Details</h4>
              <div className="prose prose-sm max-w-none">
                <p>{job.description || 'We are looking for a talented software engineer to join our platform team. You will work on building scalable infrastructure and developer tools that power our entire product suite.'}</p>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="card">
            <div className="card-content">
              <h4 className="font-medium mb-3">Requirements</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                  <span>5+ years of software engineering experience</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                  <span>Strong experience with TypeScript and React</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                  <span>Experience with Kubernetes and AWS</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                  <span>Platform engineering or DevOps experience</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Benefits */}
          <div className="card">
            <div className="card-content">
              <h4 className="font-medium mb-3">Benefits</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 bg-positive rounded-full mt-2 shrink-0" />
                  <span>Competitive salary and equity package</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 bg-positive rounded-full mt-2 shrink-0" />
                  <span>Flexible remote work arrangements</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 bg-positive rounded-full mt-2 shrink-0" />
                  <span>Health, dental, and vision insurance</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 bg-positive rounded-full mt-2 shrink-0" />
                  <span>Learning and development budget</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      label: 'Fit',
      value: 'fit',
      content: (
        <div className="space-y-6">
          {/* Resume Overlap */}
          <div className="card">
            <div className="card-content">
              <h4 className="font-medium mb-3">Resume Match</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm">Resume overlap</span>
                    <span className="font-medium">{Math.round(resumeOverlap)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-positive h-2 rounded-full transition-all duration-300"
                      style={{ width: `${resumeOverlap}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm">Must-haves</span>
                    <span className="font-medium">{mustHaves.matched}/{mustHaves.total}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-info h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(mustHaves.matched / mustHaves.total) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Skill Gaps */}
          <div className="card">
            <div className="card-content">
              <h4 className="font-medium mb-3">Gaps</h4>
              <div className="flex flex-wrap gap-2 mb-3">
                {gaps.map((gap, index) => (
                  <Badge key={index} variant="neutral">
                    {gap}
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                Consider highlighting transferable skills or learning these technologies.
              </p>
            </div>
          </div>

          {/* Recommendations */}
          <div className="card">
            <div className="card-content">
              <h4 className="font-medium mb-3">Recommendations</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <Award className="h-4 w-4 text-positive mt-0.5 shrink-0" />
                  <span>Strong match for senior-level positions</span>
                </div>
                <div className="flex items-start gap-2">
                  <TrendingUp className="h-4 w-4 text-info mt-0.5 shrink-0" />
                  <span>Consider tailoring resume to highlight platform experience</span>
                </div>
                <div className="flex items-start gap-2">
                  <Target className="h-4 w-4 text-caution mt-0.5 shrink-0" />
                  <span>Address skill gaps in cover letter</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      label: 'Strategy',
      value: 'strategy',
      content: (
        <div className="space-y-6">
          {/* Strategy Info */}
          <div className="card">
            <div className="card-content">
              <h4 className="font-medium mb-3">Strategy Analysis</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Suggested by</span>
                  <Badge variant="info">{strategy}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Confidence</span>
                  <span className="font-medium">{Math.round(confidence * 100)}%</span>
                </div>
                <div>
                  <span className="text-sm font-medium">Search Query</span>
                  <code className="block mt-1 p-2 bg-muted rounded text-xs font-mono">
                    (senior OR staff) typescript kubernetes aws platform
                  </code>
                </div>
              </div>
            </div>
          </div>

          {/* Strategy Performance */}
          <div className="card">
            <div className="card-content">
              <h4 className="font-medium mb-3">Strategy Performance</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Success Rate</span>
                  <span className="font-medium">78%</span>
                </div>
                <div className="flex justify-between">
                  <span>Avg Response Time</span>
                  <span className="font-medium">3.2 days</span>
                </div>
                <div className="flex justify-between">
                  <span>Interview Rate</span>
                  <span className="font-medium">23%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Teach the Model */}
          <div className="card">
            <div className="card-content">
              <h4 className="font-medium mb-3">Teach the Model</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Help improve recommendations by providing feedback.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Too senior', 'Too junior', 'Wrong stack', 'Bad location', 'Low compensation'].map((feedback) => (
                  <button
                    key={feedback}
                    onClick={() => handleTeach(feedback)}
                    className="btn btn-outline text-xs px-3 py-1"
                  >
                    {feedback}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <Sheet isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="flex items-start justify-between p-6 border-b border-border">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
            {job.logo_url ? (
              <img 
                src={job.logo_url} 
                alt={`${job.company} logo`}
                className="w-8 h-8 rounded"
              />
            ) : (
              <Building className="h-5 w-5 text-primary" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold">{job.title}</h2>
            <div className="text-sm text-muted-foreground flex items-center gap-4 mt-1">
              <span className="flex items-center gap-1">
                <Building className="h-3 w-3" />
                {job.company}
              </span>
              {job.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {job.location}
                </span>
              )}
              {(job.salaryMin || job.salaryMax) && (
                <span className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  {formatSalary(job.salaryMin, job.salaryMax)}
                </span>
              )}
              {job.employment_type && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {job.employment_type}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSave}>
            <Bookmark className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button variant="primary" size="sm" onClick={handleApply}>
            <Send className="h-4 w-4 mr-2" />
            Apply
          </Button>
        </div>
      </div>

      {/* Content with Tabs */}
      <div className="flex-1 overflow-hidden p-6">
        <Tabs 
          items={tabItems} 
          defaultValue="description"
          onChange={(value) => setActiveTab(value)}
        />
      </div>

      {/* Footer */}
      <div className="border-t border-border p-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={handleReject}>
            Reject
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleGenerateResume}>
              <FileText className="h-4 w-4 mr-2" />
              Generate tailored resume
            </Button>
            <Button variant="primary" onClick={handleApply}>
              Apply Now
            </Button>
          </div>
        </div>
      </div>
    </Sheet>
  );
}